/**
 * AppShellLayout — T21.
 *
 * Mantine AppShell wrapper providing:
 *   - ImpersonationBanner above the header (when impersonating)
 *   - Header: title "Meta ads", RuntimeInfoChip, Settings link, Logout button
 *   - Main: <Outlet /> for nested route content
 *
 * Responsive behavior:
 *   - ≤820px: title shrinks, RuntimeInfoChip is hidden
 *   - >820px: full layout
 */
import { Anchor, AppShell, Box, Button, Group, Title } from '@mantine/core';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../state/auth-store';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { RuntimeInfoChip } from '../RuntimeInfoChip';
import classes from './AppShellLayout.module.css';

const HEADER_HEIGHT = 56;
const BANNER_HEIGHT = 42;

export function AppShellLayout() {
  const logout = useAuthStore((s) => s.logout);
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  // The AppShell.Header is fixed-positioned, so a sibling banner above it
  // gets visually overlapped (and intercepts no clicks — the header always
  // wins z-index). Instead, host the banner INSIDE the header and grow the
  // header by BANNER_HEIGHT while impersonating.
  const headerHeight = isImpersonating ? HEADER_HEIGHT + BANNER_HEIGHT : HEADER_HEIGHT;

  return (
    <>
      <AppShell header={{ height: headerHeight }} padding="md" data-testid="app-shell">
        <AppShell.Header data-testid="app-header">
          <ImpersonationBanner />
          <Group h={HEADER_HEIGHT} px="md" justify="space-between" wrap="nowrap">
            {/* Left: title (clickable home link) */}
            <Group gap="md" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <Anchor
                component={Link}
                to="/"
                underline="never"
                c="inherit"
                data-testid="app-title-link"
              >
                <Title order={1} size="h4" style={{ whiteSpace: 'nowrap' }} data-testid="app-title">
                  Meta ads
                </Title>
              </Anchor>
            </Group>

            {/* Right: Settings + Logout */}
            <Group
              gap="sm"
              wrap="nowrap"
              className={classes.headerActions}
              data-testid="header-actions"
            >
              <Box className={classes.runtimeChipSlot} data-testid="runtime-info-chip-slot">
                <RuntimeInfoChip />
              </Box>

              <Anchor component={Link} to="/settings" size="sm" data-testid="settings-link">
                Settings
              </Anchor>

              <Button
                variant="light"
                size="xs"
                onClick={() => void handleLogout()}
                data-testid="logout-btn"
              >
                Logout
              </Button>
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Main data-testid="app-main">
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </>
  );
}
